import {
  CarIcon,
  FuelIcon,
  WrenchIcon,
  CalendarIcon,
  KeyIcon,
  GaugeIcon,
  CheckIcon,
  Edit,
  Trash2,
  X,
} from 'lucide-react'

import { DistanceMetrics } from 'src/types'

import { InfoRow } from 'src/features/InfoRow'
import { CarEditModalWindow } from 'src/features/CarEditModalWindow'
import { TooltipWrapper } from 'src/components/Tooltip'
import AlertDialogDemo from 'src/components/CustomAlertDialog'

import { useCarCard } from 'src/features/CarCard/useCarCard'
import { CardType } from 'src/features/CarCard/types'
import { ICON_SIZE } from 'src/features/CarCard/constants'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from 'src/components/shadcn/card'

export const CarCard = ({
  carNumber,
  carModel,
  carBrand,
  isActive,
  year,
  vin,
  mileage,
  lastServiceDate,
  nextMaintenance,
  engineType,
  distanceMetric,
}: CardType): JSX.Element => {
  const {
    locale,
    formatValue,
    handleFormatDate,
    handleSetActiveCar,
    handleEdit,
    handleDelete,
    lastMaintenanceInfo,
    isEditMode,
  } = useCarCard(carNumber, isActive)

  return (
    <Card
      onClick={handleSetActiveCar}
      className={`group relative w-full max-w-md mx-auto my-4 rounded-xl overflow-hidden cursor-pointer transition-all border bg-white text-gray-800 shadow-md hover:shadow-lg hover:scale-[1.02] ${
        isActive
          ? 'border-green-500 dark:border-green-500'
          : 'border-gray-300 dark:border-gray-700'
      } dark:bg-gray-900 dark:text-gray-100`}
    >
      <div
        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2 z-10"
        onClick={e => e.stopPropagation()}
      >
        <CarEditModalWindow
          carBrand={carBrand}
          carModel={carModel}
          carNumber={carNumber}
          toggle={handleEdit}
          year={year}
          isVisible={isEditMode}
          vin={vin}
          mileage={mileage}
          engineType={engineType}
          isActive={isActive}
          nextMaintenance={nextMaintenance}
          lastServiceDate={lastServiceDate}
        />
        <button
          onClick={handleEdit}
          className="group p-1 rounded transition-colors"
        >
          <Edit size={20} className="text-gray-600 group-hover:text-blue-500" />
        </button>
        <AlertDialogDemo
          trigger={
            <button className="group p-1 rounded transition-colors">
              <Trash2
                size={20}
                className="text-gray-600 group-hover:text-red-500"
              />
            </button>
          }
          onConfirm={handleDelete}
          accept={locale.alert.accept}
          description={locale.alert.description}
          title={locale.alert.title}
          cancel={locale.alert.cancel}
          isDanger
        />
      </div>
      <CardHeader className="flex items-center gap-3 px-5 py-4 bg-gray-100 dark:bg-gray-800 border-b dark:border-gray-700">
        <CarIcon size={28} className="text-blue-500" />
        <CardTitle className="text-lg font-semibold">
          {carBrand} {carModel}{' '}
          <span className="text-sm text-gray-500 dark:text-gray-400">
            ({year})
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-5 space-y-3 text-sm">
        <InfoRow
          icon={<KeyIcon size={ICON_SIZE} className="text-indigo-500" />}
          label={locale.carNumber}
          value={formatValue(carNumber)}
        />
        <InfoRow
          icon={
            isActive ? (
              <CheckIcon size={ICON_SIZE} className="text-green-500" />
            ) : (
              <X size={ICON_SIZE} className="text-red-500" />
            )
          }
          label={locale.isActive}
          value={isActive ? locale.activeYes : locale.activeNo}
        />
        <InfoRow
          icon={<GaugeIcon size={ICON_SIZE} className="text-orange-500" />}
          label={locale.mileage}
          value={
            formatValue(mileage) + `(${distanceMetric || DistanceMetrics.KM})`
          }
        />
        <InfoRow
          icon={<WrenchIcon size={ICON_SIZE} className="text-pink-500" />}
          label={locale.vin}
          value={formatValue(vin)}
        />
        <InfoRow
          icon={<FuelIcon size={ICON_SIZE} className="text-teal-500" />}
          label={locale.engineType}
          value={formatValue(engineType)}
        />
        <TooltipWrapper text={lastMaintenanceInfo}>
          <div>
            <InfoRow
              icon={
                <CalendarIcon size={ICON_SIZE} className="text-purple-500" />
              }
              label={locale.lastServiceDate}
              value={handleFormatDate(lastServiceDate?.seconds)}
            />
          </div>
        </TooltipWrapper>
        <InfoRow
          icon={<CalendarIcon size={ICON_SIZE} className="text-purple-500" />}
          label={locale.nextMaintenance}
          value={handleFormatDate(nextMaintenance?.seconds)}
        />
      </CardContent>
    </Card>
  )
}
